import React, { memo, useMemo, useRef } from "react";
import LinkPreview from "./LinkPreview";
import Code from "./Code";
import remarkGfm from "remark-gfm";
import ReactMarkdown, { type Components } from "react-markdown";
import { safeUrl } from "utils/safeHtml";

type MarkdownViewerProps = {
  uuid: string;
  handleApplyAi?: (model: string) => Promise<void>;
  message: string;
  /**
   * `true` while the AI is still writing the response (streaming or
   * post-stream parsing). Downstream components use this to disable
   * interactive controls (e.g. the BPMN "apply" action) so the user
   * can't fire them against a half-finished model — that was causing
   * the backend to reject the apply with the original/incomplete
   * XML.
   */
  isStreaming?: boolean;
  /**
   * Whether the current user is an administrator of the selected
   * organization. When `false`, the BPMN preview's "Azioni" / "Apply"
   * controls are disabled.
   */
  isAdmin?: boolean;
};

const MarkdownViewer: React.FC<MarkdownViewerProps> = ({
  uuid,
  handleApplyAi,
  message,
  isStreaming = false,
  isAdmin,
}) => {
  // The per-render values needed inside the react-markdown component
  // renderers (`code` and `a`) are kept in a ref so the `components`
  // object below can be created ONCE and stay referentially stable
  // for the lifetime of this MarkdownViewer.
  //
  // Why this matters: react-markdown uses each entry of `components`
  // (e.g. `components.code`) as the React element `type` for the
  // matching markdown node. If `components` (and therefore the
  // `code` function) is recreated on every render, the element type
  // changes identity on every render and React UNMOUNTS and REMOUNTS
  // the entire `Code` subtree — resetting its local state. During
  // streaming, `message` changes on every chunk, so this happened
  // constantly: the user opened the BPMN preview dialog, then 1-2
  // seconds later the AI streamed the trailing explanation prose,
  // the next chunk triggered a remount, the `openPreview` state
  // reset to `false`, and the dialog closed by itself. Keeping the
  // `code` function stable means React reconciles `Code` in place
  // (same type + same `code-0` key) and preserves the dialog state.
  const ctxRef = useRef({ uuid, handleApplyAi, isStreaming, isAdmin });
  ctxRef.current = { uuid, handleApplyAi, isStreaming, isAdmin };

  const components = useMemo<Components>(
    () => ({
      a: ({ href, children, ...rest }) => {
        const safeHref = safeUrl(href);
        if (!safeHref) return <span>{children}</span>;
        return (
          <LinkPreview
            key={`link-preview-${ctxRef.current.uuid}`}
            {...rest}
            href={safeHref}
          >
            {children}
          </LinkPreview>
        );
      },
      code: (props) => (
        <Code
          {...props}
          uuid={ctxRef.current.uuid}
          onApply={ctxRef.current.handleApplyAi}
          isStreaming={ctxRef.current.isStreaming}
          isAdmin={ctxRef.current.isAdmin}
        />
      ),
    }),
    []
  );

  return (
    <ReactMarkdown
      key={"markdown" + uuid}
      remarkPlugins={[remarkGfm]}
      components={components}
    >
      {message}
    </ReactMarkdown>
  );
};

export default memo(MarkdownViewer);
